import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  ArrowRight, Check, X, Users, GraduationCap, ClipboardList,
  MessageSquareText, Target, Star, Award, Timer, Coffee, Sparkles,
  UserCheck, Handshake,
} from "lucide-react";

const useReveal = () => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const n = ref.current; if (!n) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) { setInView(true); return; }
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.unobserve(n); } }, { threshold: 0.15, rootMargin: "0px 0px -60px 0px" });
    obs.observe(n); return () => obs.disconnect();
  }, []);
  return { ref, inView };
};

/* ============ HERO ============ */
const HERO_IMAGE = "/mock/hero.png"; // to be provided

const FloatCard = ({ Icon, title, pos, delay, inView, tint = "text-[#1d4ed8]" }) => (
  <div className={`absolute ${pos} z-10 medily-float-slow`} style={{
    transform: inView ? "translate3d(0,0,0) scale(1)" : "translate3d(0,20px,0) scale(0.9)",
    opacity: inView ? 1 : 0,
    transition: `transform 900ms cubic-bezier(0.22,1,0.36,1) ${delay}ms, opacity 700ms cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
  }}>
    <div className="flex items-center gap-2.5 rounded-2xl bg-white border border-white px-3.5 py-2.5 shadow-[0_25px_55px_-15px_rgba(29,78,216,0.4)] backdrop-blur">
      <span className={`w-8 h-8 rounded-lg bg-[#eef4ff] flex items-center justify-center ${tint}`}><Icon className="w-4 h-4" /></span>
      <span className="text-[12.5px] font-semibold text-slate-800 whitespace-nowrap">{title}</span>
    </div>
  </div>
);

const HeroVisual = ({ inView }) => {
  const [ok, setOk] = useState(true);
  return (
    <div className="relative w-full">
      <div className="absolute -inset-8 -z-10 rounded-[56px] bg-gradient-to-br from-[#c9dcff] via-[#dbe6ff] to-transparent blur-3xl opacity-95" />
      <div style={{
        transform: inView ? "translate3d(0,0,0) rotate(0) scale(1)" : "translate3d(-30px,20px,0) rotate(-2deg) scale(0.97)",
        opacity: inView ? 1 : 0,
        transition: "transform 1100ms cubic-bezier(0.22,1,0.36,1) 120ms, opacity 900ms cubic-bezier(0.22,1,0.36,1) 120ms",
      }} className="rounded-[28px] overflow-hidden shadow-[0_55px_120px_-25px_rgba(29,78,216,0.5)] border border-white">
        {ok ? (
          <img src={HERO_IMAGE} alt="Students in an online MMI with interviewers" className="w-full h-auto block object-cover"
            onError={() => setOk(false)} draggable="false" data-testid="mock-hero-illustration" />
        ) : (
          <div className="w-full aspect-[16/11] bg-gradient-to-br from-[#dbe6ff] via-[#eef4ff] to-white flex items-center justify-center">
            <span className="text-[11px] uppercase tracking-[0.16em] text-[#1d4ed8]/60 font-medium">Awaiting hero illustration</span>
          </div>
        )}
      </div>
      <FloatCard Icon={GraduationCap} title="University-Specific Feedback" pos="top-4 -left-4 md:-left-8" delay={480} inView={inView} />
      <FloatCard Icon={Target} title="12 MMI Stations" pos="top-8 -right-3 md:top-10 md:-right-8" delay={620} inView={inView} />
      <FloatCard Icon={Users} title="Small Groups of 4" pos="-bottom-5 left-6 md:left-12" delay={760} inView={inView} tint="text-[#15803d]" />
      <FloatCard Icon={UserCheck} title="Experienced Interviewers" pos="bottom-16 -right-4 md:-right-6" delay={900} inView={inView} tint="text-amber-500" />
    </div>
  );
};

const Hero = () => {
  const { ref, inView } = useReveal();
  return (
    <section ref={ref} className="relative overflow-hidden" data-testid="mock-hero-section">
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-40 -left-40 w-[720px] h-[720px] bg-[#bcd3ff] rounded-full blur-3xl opacity-90" />
        <div className="absolute top-24 -right-40 w-[640px] h-[640px] bg-[#d5e4ff] rounded-full blur-3xl opacity-95" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.65),transparent_60%)]" />
      </div>
      <div className="max-w-7xl mx-auto px-6 pt-20 md:pt-24 pb-28 md:pb-36 grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center">
        <div style={{
          transform: inView ? "translate3d(0,0,0)" : "translate3d(0,28px,0)",
          opacity: inView ? 1 : 0,
          transition: "transform 900ms cubic-bezier(0.22,1,0.36,1), opacity 700ms cubic-bezier(0.22,1,0.36,1)",
        }}>
          <span className="inline-block px-3 py-1 rounded-full bg-white/70 backdrop-blur border border-[#dbe6ff] text-[#1d4ed8] text-[12px] font-semibold tracking-[0.14em] uppercase shadow-sm">University-Specific Mock MMI</span>
          <h1 className="mt-6 font-semibold text-slate-900 leading-[1.02] tracking-tight text-[46px] md:text-[62px]" data-testid="mock-hero-heading">
            Practise for the{" "}
            <span className="relative inline-block whitespace-nowrap">
              <span className="relative z-10 text-[#1d4ed8]">university you’re aiming for</span>
              <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#dbe6ff]/70 -z-0 rounded-sm" />
              <span className="relative z-10">.</span>
            </span>
          </h1>
          <p className="mt-6 text-slate-500 text-[17px] leading-relaxed max-w-[600px]">
            Unlike generic mock interviews, Medily provides feedback tailored to your chosen university,
            helping you understand the qualities, communication style and interview approach that your
            chosen medical school typically looks for in successful applicants.
          </p>
        </div>
        <div><HeroVisual inView={inView} /></div>
      </div>
    </section>
  );
};

/* ============ WHY UNIVERSITY-SPECIFIC ============ */
const Why = () => {
  const { ref, inView } = useReveal();
  const copyStyle = {
    transform: inView ? "translate3d(0,0,0)" : "translate3d(0,28px,0)",
    opacity: inView ? 1 : 0,
    transition: "transform 900ms cubic-bezier(0.22,1,0.36,1), opacity 700ms cubic-bezier(0.22,1,0.36,1)",
  };
  return (
    <section ref={ref} className="max-w-7xl mx-auto px-6 pt-24 md:pt-32 pb-16" data-testid="mock-why-section">
      <div style={copyStyle} className="max-w-3xl mx-auto text-center">
        <span className="inline-block px-3 py-1 rounded-full bg-[#eef4ff] text-[#1d4ed8] text-[12px] font-medium">Why University-Specific?</span>
        <h2 className="mt-5 text-[34px] md:text-[52px] font-semibold text-slate-900 tracking-tight leading-[1.05]">
          Not all medical schools assess candidates in the{" "}
          <span className="relative inline-block whitespace-nowrap">
            <span className="relative z-10 text-[#1d4ed8]">same way</span>
            <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#dbe6ff]/70 -z-0 rounded-sm" />
          </span>.
        </h2>
        <p className="mt-6 text-slate-500 text-[16.5px] leading-relaxed">
          While every medical school is looking for compassionate, capable future doctors, each
          university places different emphasis on qualities such as communication, ethical reasoning,
          reflection and structure.
        </p>
        <p className="mt-4 text-slate-500 text-[16.5px] leading-relaxed">
          That’s why every Medily mock interview is built around your chosen university, ensuring
          the feedback you receive is tailored to what your medical school typically values.
        </p>
      </div>

      {/* Comparison card */}
      <div style={copyStyle} className="mt-14 relative">
        <div className="absolute -inset-6 -z-10 rounded-[40px] bg-gradient-to-br from-[#c9dcff] via-[#dbe6ff] to-transparent blur-3xl opacity-95" />
        <div className="rounded-[28px] bg-white border border-white shadow-[0_50px_110px_-25px_rgba(29,78,216,0.55)] p-6 md:p-10 grid md:grid-cols-[1fr_auto_1fr] gap-6 md:gap-8 items-stretch">
          {/* Generic */}
          <div className="rounded-[22px] border border-slate-100 bg-[#f9fafb] p-6 md:p-8">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-2xl bg-slate-100 text-slate-500 flex items-center justify-center"><X className="w-5 h-5" /></span>
              <p className="text-[13px] font-bold tracking-[0.18em] uppercase text-slate-500">Generic mock interviews</p>
            </div>
            <ul className="mt-5 space-y-3">
              {[
                "Generic feedback",
                "Same interview approach for every applicant",
                "Same advice for every student",
                "One-size-fits-all preparation",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-[14.5px] text-slate-500">
                  <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center"><X className="w-3 h-3" /></span>
                  {t}
                </li>
              ))}
            </ul>
          </div>

          {/* Divider */}
          <div className="hidden md:flex flex-col items-center">
            <div className="flex-1 w-px bg-gradient-to-b from-transparent via-slate-200 to-transparent" />
            <span className="px-3 py-1.5 rounded-full bg-[#1d4ed8]/10 text-[#1d4ed8] text-[11px] font-bold tracking-widest uppercase whitespace-nowrap">vs</span>
            <div className="flex-1 w-px bg-gradient-to-b from-transparent via-slate-200 to-transparent" />
          </div>

          {/* Medily */}
          <div className="rounded-[22px] bg-gradient-to-br from-[#1d4ed8] via-[#2555df] to-[#3b6ff5] text-white p-6 md:p-8 relative overflow-hidden shadow-[0_40px_90px_-25px_rgba(29,78,216,0.55)]">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-2xl bg-white/15 text-white flex items-center justify-center"><Star className="w-5 h-5 fill-current" /></span>
              <p className="text-[13px] font-bold tracking-[0.18em] uppercase text-white/85">Medily University-Specific Mock MMI</p>
            </div>
            <ul className="mt-5 space-y-3">
              {[
                "Feedback tailored to your chosen university",
                "Personalised feedback based on your individual strengths and weaknesses",
                "Interviewers briefed on your selected university’s interview style and commonly assessed qualities",
                "Feedback focused on the qualities your chosen medical school typically values",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-[14.5px] text-white/95">
                  <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-white/20 text-white flex items-center justify-center"><Check className="w-3 h-3" /></span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============ INTERVIEW JOURNEY ============ */
const JOURNEY = [
  { id: "welcome", icon: Handshake, title: "Welcome & Introduction", body: "Meet your interviewers, receive instructions and settle into the session." },
  { id: "circuit-a", icon: ClipboardList, title: "Circuit A", body: "Complete six realistic MMI stations." },
  { id: "break", icon: Coffee, title: "Short Break", body: "" },
  { id: "circuit-b", icon: ClipboardList, title: "Circuit B", body: "Complete a further six realistic MMI stations." },
  { id: "feedback", icon: MessageSquareText, title: "Personal Feedback", body: "Receive personalised feedback tailored to your chosen university, including strengths, areas for improvement and an individual score for every station." },
  { id: "debrief", icon: Users, title: "Live Interviewer Q&A Debrief", body: "Discuss common mistakes, ask questions and reflect on your performance." },
];

const JourneyRow = ({ item, i, last }) => {
  const { ref, inView } = useReveal();
  const Icon = item.icon;
  const style = {
    transform: inView ? "translate3d(0,0,0)" : "translate3d(-24px,0,0)",
    opacity: inView ? 1 : 0,
    transition: `transform 900ms cubic-bezier(0.22,1,0.36,1) ${i * 90}ms, opacity 700ms cubic-bezier(0.22,1,0.36,1) ${i * 90}ms`,
  };
  return (
    <div ref={ref} style={style} className="relative pl-16 md:pl-20" data-testid={`mock-journey-${item.id}`}>
      {/* Rail */}
      {!last && <span className="absolute left-[22px] md:left-[26px] top-14 bottom-[-32px] w-[2px] bg-gradient-to-b from-[#1d4ed8]/50 via-[#1d4ed8]/25 to-transparent" />}
      {/* Node */}
      <span className="absolute left-0 top-0 w-11 md:w-[52px] h-11 md:h-[52px] rounded-2xl bg-gradient-to-br from-[#1d4ed8] to-[#3b6ff5] text-white flex items-center justify-center shadow-[0_20px_45px_-15px_rgba(29,78,216,0.6)]">
        <Icon className="w-5 h-5 md:w-6 md:h-6" />
      </span>
      <div className="rounded-2xl bg-white border border-slate-100 p-5 md:p-6 shadow-[0_20px_50px_-25px_rgba(29,78,216,0.35)] hover:shadow-[0_30px_65px_-25px_rgba(29,78,216,0.5)] hover:-translate-y-1 transition-all duration-500">
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-bold tracking-[0.22em] uppercase text-[#1d4ed8]">Stage {String(i + 1).padStart(2, "0")}</span>
        </div>
        <h4 className="mt-1 text-[19px] md:text-[21px] font-semibold text-slate-900 tracking-tight">{item.title}</h4>
        {item.body && <p className="mt-2 text-[14.5px] text-slate-600 leading-relaxed">{item.body}</p>}
      </div>
    </div>
  );
};

const Journey = () => (
  <section className="max-w-3xl mx-auto px-6 pt-24 md:pt-28 pb-8" data-testid="mock-journey-section">
    <div className="text-center max-w-2xl mx-auto">
      <span className="inline-block px-3 py-1 rounded-full bg-[#eef4ff] text-[#1d4ed8] text-[12px] font-medium">Your Interview Journey</span>
      <h2 className="mt-5 text-[34px] md:text-[46px] font-semibold text-slate-900 tracking-tight leading-[1.05]">
        Know exactly what to expect.
      </h2>
    </div>
    <div className="mt-12 space-y-8">
      {JOURNEY.map((s, i) => <JourneyRow key={s.id} item={s} i={i} last={i === JOURNEY.length - 1} />)}
    </div>
  </section>
);

/* ============ PASTEL IMAGE GALLERY (assets pending) ============ */
const MOCK_GALLERY = [
  { src: "/mock/gallery-1.png", alt: "" },
  { src: "/mock/gallery-2.png", alt: "" },
  { src: "/mock/gallery-3.png", alt: "" },
  { src: "/mock/gallery-4.png", alt: "" },
];

const GalleryTile = ({ item, i, className }) => {
  const { ref, inView } = useReveal();
  const [ok, setOk] = useState(true);
  return (
    <div
      ref={ref}
      style={{
        transform: inView ? "translate3d(0,0,0) scale(1)" : "translate3d(0,22px,0) scale(0.98)",
        opacity: inView ? 1 : 0,
        transition: `transform 900ms cubic-bezier(0.22,1,0.36,1) ${i * 100}ms, opacity 700ms cubic-bezier(0.22,1,0.36,1) ${i * 100}ms`,
      }}
      className={`group relative rounded-[24px] overflow-hidden border border-white bg-white shadow-[0_25px_60px_-25px_rgba(29,78,216,0.35)] hover:shadow-[0_40px_80px_-25px_rgba(29,78,216,0.55)] hover:-translate-y-1 hover:scale-[1.015] transition-all duration-500 ${className}`}
      data-testid={`mock-gallery-tile-${i}`}
    >
      {ok ? (
        <img src={item.src} alt={item.alt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" onError={() => setOk(false)} draggable="false" />
      ) : (
        <div className="w-full h-full min-h-[220px] bg-gradient-to-br from-[#eef4ff] via-white to-[#f7faff] flex items-center justify-center">
          <span className="text-[11px] uppercase tracking-[0.16em] text-[#1d4ed8]/60 font-medium">Awaiting image {i + 1}</span>
        </div>
      )}
    </div>
  );
};

const PastelGallery = () => {
  // Square-ish premium collage from 4 images:
  //  +----------+---+
  //  |   1      | 2 |   (row 1: 2/3 + 1/3 · 16:10 + 4:5)
  //  +----+-----+---+
  //  | 3  |    4    |   (row 2: 1/3 + 2/3 · 4:5 + 16:10)
  //  +----+---------+
  const layout = [
    "col-span-2 aspect-[16/10]",  // 1 wide
    "col-span-1 aspect-[4/5]",    // 2 upright
    "col-span-1 aspect-[4/5]",    // 3 upright
    "col-span-2 aspect-[16/10]",  // 4 wide
  ];
  return (
    <section className="py-20 md:py-28" data-testid="mock-gallery-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="mx-auto max-w-[960px]">
          <div className="grid grid-cols-3 gap-4 md:gap-6">
            {MOCK_GALLERY.map((t, i) => (
              <GalleryTile key={i} item={t} i={i} className={layout[i]} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============ EVERYTHING INCLUDED ============ */
const FEATURES = [
  { id: "uni-feedback", icon: Star, title: "University-Specific Feedback", body: "Receive personalised feedback tailored to the interview style and expectations of your chosen university.", featured: true },
  { id: "stations", icon: ClipboardList, title: "12 Realistic MMI Stations", body: "Two realistic six-station circuits." },
  { id: "small-groups", icon: Users, title: "Small Groups of Four", body: "Maximum of four students per station for a realistic and engaging experience." },
  { id: "interviewers", icon: UserCheck, title: "Experienced Interviewers", body: "Experienced interviewers familiar with UK medical school MMIs." },
  { id: "individual", icon: MessageSquareText, title: "Individual Feedback", body: "Detailed feedback for every station." },
  { id: "strengths", icon: Target, title: "Personal Strengths & Areas for Improvement", body: "Know exactly what to improve before interview day." },
  { id: "scores", icon: Award, title: "MMI Station Scores", body: "Receive a score for every station to help track your performance throughout the interview." },
  { id: "debrief", icon: Handshake, title: "Live Q&A Debrief", body: "Ask questions and receive advice directly after completing your interview." },
];

const FeatureCard = ({ f, i }) => {
  const { ref, inView } = useReveal();
  const Icon = f.icon;
  return (
    <div ref={ref} style={{
      transform: inView ? "translate3d(0,0,0)" : "translate3d(0,22px,0)",
      opacity: inView ? 1 : 0,
      transition: `transform 900ms cubic-bezier(0.22,1,0.36,1) ${i * 80}ms, opacity 700ms cubic-bezier(0.22,1,0.36,1) ${i * 80}ms`,
    }} className={`relative rounded-3xl p-6 md:p-8 border transition-all duration-500 hover:-translate-y-1 ${
      f.featured
        ? "md:col-span-2 bg-gradient-to-br from-[#1d4ed8] via-[#2555df] to-[#3b6ff5] text-white border-transparent shadow-[0_45px_100px_-25px_rgba(29,78,216,0.7)]"
        : "bg-white text-slate-900 border-slate-100 shadow-[0_20px_50px_-25px_rgba(29,78,216,0.35)]"
    }`} data-testid={`mock-feature-${f.id}`}>
      {f.featured && (
        <span className="absolute -top-3 right-6 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white text-[#1d4ed8] text-[10px] font-bold tracking-wider uppercase shadow-md">
          <Star className="w-2.5 h-2.5 fill-current" /> Signature feature
        </span>
      )}
      <div className="flex items-center justify-between">
        <span className={`w-11 h-11 rounded-2xl flex items-center justify-center ${f.featured ? "bg-white/15 text-white" : "bg-[#eaf1ff] text-[#1d4ed8]"}`}><Icon className="w-5 h-5" /></span>
      </div>
      <h4 className={`mt-5 font-semibold tracking-tight leading-tight ${f.featured ? "text-[26px] md:text-[30px]" : "text-[18px]"}`}>{f.title}</h4>
      <p className={`mt-2 leading-relaxed ${f.featured ? "text-white/85 text-[14.5px] max-w-[560px]" : "text-slate-600 text-[14px]"}`}>{f.body}</p>
    </div>
  );
};

const Features = () => (
  <section className="max-w-7xl mx-auto px-6 pt-8 pb-24" data-testid="mock-features-section">
    <div className="text-center max-w-2xl mx-auto">
      <span className="inline-block px-3 py-1 rounded-full bg-[#eef4ff] text-[#1d4ed8] text-[12px] font-medium">Everything Included</span>
      <h2 className="mt-4 text-[34px] md:text-[46px] font-semibold text-slate-900 tracking-tight leading-[1.05]">
        Every detail, on interview day.
      </h2>
    </div>
    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {FEATURES.map((f, i) => <FeatureCard key={f.id} f={f} i={i} />)}
    </div>
  </section>
);

/* ============ FINAL CTA ============ */
const FinalCta = () => {
  const navigate = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="mock-final-cta-section">
      <div className="rounded-[32px] bg-gradient-to-br from-[#eef4ff] via-white to-[#dbe6ff] border border-white p-12 md:p-20 text-center shadow-[0_45px_110px_-30px_rgba(29,78,216,0.55)]">
        <h3 className="text-[34px] md:text-[52px] font-semibold text-slate-900 tracking-tight leading-[1.05]">
          Ready to experience the{" "}
          <span className="relative inline-block whitespace-nowrap">
            <span className="relative z-10 text-[#1d4ed8]">real thing</span>
            <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#dbe6ff]/70 -z-0 rounded-sm" />
          </span>?
        </h3>
        <p className="mt-5 text-slate-600 max-w-2xl mx-auto text-[16.5px] leading-relaxed">
          Experience a realistic University-Specific Mock MMI, receive tailored feedback and walk into
          your interview knowing exactly what to improve.
        </p>
        <div className="mt-9 flex justify-center">
          <button onClick={() => navigate("/pricing")} className="px-8 py-4 rounded-full bg-[#1d4ed8] text-white font-medium hover:bg-[#1e40af] transition shadow-[0_18px_45px_-12px_rgba(29,78,216,0.6)] inline-flex items-center gap-2" data-testid="mock-final-cta-btn">
            Book Your Mock MMI <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

const MockMMI = () => (
  <div className="bg-white min-h-screen">
    <Navbar />
    <Hero />
    <Why />
    <Journey />
    <PastelGallery />
    <Features />
    <FinalCta />
    <Footer />
  </div>
);

export default MockMMI;
